/**
 * ATL-1445 — the app's side of Atlas API v1 drafts (`/api-drafts`): the sale and acceptance drafts an
 * AI agent proposed, which a person applies through the app's own screens and writers.
 *
 * A body without the fields the screen depends on is refused loudly, rather than rendered as an empty
 * list that would read as "no drafts".
 */
import type { AxiosInstance } from 'axios'
import apiClient from '../API/apiClient'

export type ApiDraftKind = 'sale' | 'acceptance'
export type ApiDraftStatus = 'proposed' | 'expired' | 'applied' | 'dismissed'

export interface ApiDraftLine {
  productId: string
  name: string | null
  quantity: number
  purchasePrice?: number
}

export interface ApiDraft {
  id: string
  kind: ApiDraftKind
  status: ApiDraftStatus
  store: { id: string; name: string | null }
  items: ApiDraftLine[]
  note: string | null
  createdAt: string
  expiresAt: string
  proposedBy: { tokenId: string; tokenName: string | null; name: string | null }
  decidedAt: string | null
  decidedBy: { name: string | null } | null
  appliedRecordId: string | null
}

const isDraft = (d: unknown): d is ApiDraft =>
  !!d &&
  typeof (d as ApiDraft).id === 'string' &&
  ((d as ApiDraft).kind === 'sale' || (d as ApiDraft).kind === 'acceptance') &&
  typeof (d as ApiDraft).status === 'string' &&
  !!(d as ApiDraft).store &&
  typeof (d as ApiDraft).store.id === 'string' &&
  Array.isArray((d as ApiDraft).items)

const draftOrThrow = (d: unknown): ApiDraft => {
  if (!isDraft(d)) throw new Error('api draft response is malformed')
  return d
}

export const createApiDraftsApi = (http: AxiosInstance) => ({
  /** The drafts this person can act on; by default the open ones. */
  async list(opts: { status?: ApiDraftStatus; kind?: ApiDraftKind } = {}): Promise<ApiDraft[]> {
    const { data } = await http.get('/api-drafts', { params: opts })
    const items = (data as { items?: unknown } | null)?.items
    if (!Array.isArray(items)) throw new Error('api drafts response is malformed')
    return items.map(draftOrThrow)
  },

  /** Report the sale/supply just saved from this draft. The backend verifies it in Firestore. */
  async reportApplied(draftId: string, firestoreId: string): Promise<ApiDraft> {
    const { data } = await http.post(`/api-drafts/${encodeURIComponent(draftId)}/applied`, { firestoreId })
    return draftOrThrow(data)
  },

  async dismiss(draftId: string): Promise<ApiDraft> {
    const { data } = await http.post(`/api-drafts/${encodeURIComponent(draftId)}/dismiss`, {})
    return draftOrThrow(data)
  },
})

/** Production instance bound to the shared authenticated axios client. */
export const apiDraftsApi = createApiDraftsApi(apiClient)
export type ApiDraftsApi = ReturnType<typeof createApiDraftsApi>
